import React from "react";
import { styled } from "@mui/material/styles";
import { Box, Dialog, DialogContent, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import ButtonBlue from "../../../../shared/general/ButtonBlue";
import TextFieldTheme from "../../../../shared/general/TextFieldTheme";

const StyledDialog = styled(Dialog)({
  "& .MuiDialogContent-root": {
    padding: 0,
    "& .content": {
      padding: 16,
      textAlign: "center",
      "& .warning-icon-label": {
        fontSize: 90,
        color: "#ffab00",
      },
      "& .header-label": {
        padding: "20px 0",
      },
      "& .detail": {
        fontSize: 16,
        "& span": {
          color: "#ffab00",
          fontSize: 20,
        },
      },
      "& .date-picker": {
        marginTop: 20,
        maxWidth: 250,
        marginLeft: "auto",
        marginRight: "auto",
      },
      "& .date-error": {
        marginTop: 6,
        fontSize: 14,
        color: "#d32f2f",
        whiteSpace: "nowrap",
      },
    },
  },
});

const DATE_FORMAT = "DD/MM/YYYY";

const DialogConfirmWithDate = (props) => {
  const {
    open,
    handleClose,
    text,
    text1,
    text2,
    handleUpdateAdmin,
    date,
    onChangeDate,
  } = props;
  const { t } = useTranslation();

  const startOfMonth = dayjs().startOf("month");
  const minDate = props.minDate || startOfMonth;
  const maxDate = props.maxDate || startOfMonth.add(1, "year");

  const getDateError = () => {
    if (!date) {
      return { isError: true, message: null };
    }
    const selected = dayjs(date);
    if (!selected.isValid()) {
      return { isError: true, message: t("InvalidDate") };
    }
    if (
      selected.isBefore(minDate, "day") ||
      selected.isAfter(maxDate, "day")
    ) {
      return {
        isError: true,
        message: t("selectDateBetween", {
          min: minDate.format(DATE_FORMAT),
          max: maxDate.format(DATE_FORMAT),
        }),
      };
    }
    return { isError: false, message: null };
  };

  const dateError = getDateError();

  return (
    <StyledDialog fullWidth maxWidth={"xs"} open={open} onClose={handleClose}>
      <DialogContent>
        <div
          style={{ width: "100%", height: 6, backgroundColor: "#ffab00" }}
        ></div>
        <div className="content">
          <WarningAmberRoundedIcon className="warning-icon-label" />
          <Typography variant="h5" className="header-label" color="text.third">
            {t("AreYouSure")}
          </Typography>

          <Typography className="detail" variant="h6" color="text.secondary">
            {text}
          </Typography>
          <Typography className="detail" variant="h6" color="text.secondary">
            {text1}
          </Typography>
          <Typography className="detail" variant="h6" color="text.third">
            {text2}
          </Typography>

          <Box className="date-picker">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={date}
                minDate={minDate}
                maxDate={maxDate}
                inputFormat={DATE_FORMAT}
                onChange={(newValue) => onChangeDate(newValue)}
                renderInput={(params) => (
                  <TextFieldTheme
                    {...params}
                    fullWidth
                    size="small"
                    error={dateError.isError}
                  />
                )}
              />
            </LocalizationProvider>
          </Box>
          {dateError.message && (
            <Typography className="date-error">{dateError.message}</Typography>
          )}

          <Box
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <ButtonBlue
              color="secondary"
              variant="text"
              style={{ marginRight: "10px" }}
              onClick={handleClose}
            >
              {t("Cancel")}
            </ButtonBlue>
            <ButtonBlue
              color="warning"
              variant="contained"
              onClick={handleUpdateAdmin}
              disabled={dateError.isError}
            >
              {t("SaveChanges")}
            </ButtonBlue>
          </Box>
        </div>
      </DialogContent>
    </StyledDialog>
  );
};

export default DialogConfirmWithDate;
